import Socket from '../connection/socket';
import { getState } from './socket-listener-main';
import { GreenroomState } from '../stores/types/types';
import OpenTok from '../utils/video/opentok-connection';
import {
	ClientOpentokHealth,
	HealthStatsMessage,
} from '../stores/types/opentok-health';

/**
 * On-demand OpenTok health reporting for the producer health dashboard.
 *
 * The dashboard runs on its own route with no media, so it cannot see anyone's
 * WebRTC stats. It broadcasts a request on the meeting channel and each client
 * answers once with its own view. Nothing is sent while no dashboard is open,
 * which matters because socket messages fan out to every client in the channel.
 *
 * Clients also push a report unprompted when the SDK reports a degradation, so a
 * problem shows up without waiting for the dashboard's next request.
 */

/** Ignore repeat requests inside this window so N dashboards cannot amplify. */
const REQUEST_THROTTLE_MS = 1500;

/** Floor between unprompted degradation pushes. */
const DEGRADATION_PUSH_THROTTLE_MS = 5000;

let lastReportAt = 0;

export function resetHealthStatsThrottleForTests(): void {
	lastReportAt = 0;
}

/** Reads the identity a report is attributed to from redux. */
function reportIdentity(): Pick<ClientOpentokHealth, 'grUser' | 'name' | 'isProducer' | 'webrtcID'> {
	const {
		SessionReducer: { user, host, webrtcID },
	} = getState() as GreenroomState;

	return {
		grUser: user?.gr_user ?? null,
		name: user ? `${user.first_name} ${user.last_name}`.trim() : 'unknown',
		isProducer: !!host,
		webrtcID: webrtcID ?? undefined,
	};
}

export async function buildHealthReport(opentok: OpenTok): Promise<ClientOpentokHealth> {
	const collected = await opentok.collectHealthStats();

	return {
		...reportIdentity(),
		collectedAt: Date.now(),
		sessionId: collected.sessionId,
		cpuPerformanceState: collected.cpuPerformanceState,
		publishers: collected.publishers,
		subscribers: collected.subscribers,
	};
}

async function sendReport(
	socket: Socket,
	opentok: OpenTok | null,
	throttleMs: number,
): Promise<void> {
	if (!opentok) return;

	const now = Date.now();
	if (now - lastReportAt < throttleMs) return;
	lastReportAt = now;

	try {
		const report = await buildHealthReport(opentok);
		socket.sendMessage(HealthStatsMessage.Report, report);
	} catch (error) {
		// A failed stat read must never take down the meeting socket.
		console.error('health-stats: could not build report', error);
	}
}

/**
 * Registers the responder. Call with the room's OpenTok instance once it exists;
 * `getOpentok` is a getter rather than the instance because the OpenTok object is
 * recreated on reconnect and media-region changes.
 */
export default function HealthStats(socket: Socket, getOpentok: () => OpenTok | null) {
	// Returns the promise so callers (and tests) can await the reply; the socket
	// layer ignores return values.
	socket.addListener(
		HealthStatsMessage.Request,
		() => sendReport(socket, getOpentok(), REQUEST_THROTTLE_MS),
	);

	const opentok = getOpentok();
	if (opentok) {
		opentok.onHealthDegradation = () => {
			sendReport(socket, getOpentok(), DEGRADATION_PUSH_THROTTLE_MS);
		};
	}
}
